import { Component, DestroyRef, effect, inject, input, linkedSignal, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmAutocompleteImports, provideHlmAutocompleteConfig } from '@spartan-ng/helm/autocomplete';
import { HlmButtonGroupImports } from '@spartan-ng/helm/button-group';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmSelectImports } from '@spartan-ng/helm/select';

import { Aptitude } from '../../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { AptitudeStore } from '../../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/store/aptitude.store';
import { ButtonAddNewComponent } from '../../../../../shared/components/button-add-new/button-add-new.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { FormControlNames } from '../../../../models/form-control-names.model';
import { EtapeDetailFormService } from '../../étape-detail-form.service';
import { Référentiel } from '../../../../../administration/gestion-référentiels/models/référentiel.model';
import { ReferentielStore } from '../../../../../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-form-detail-objectifs-aptitudes',
  imports: [
    ReactiveFormsModule,
    ButtonAddNewComponent,
    ExpansionPanel,
    HlmSelectImports,
    BrnSelectImports,
    HlmAutocompleteImports,
    HlmFieldImports,
    HlmButtonGroupImports,
  ],
  templateUrl: './form-detail-objectifs-aptitudes.component.html',
  providers: [
    provideHlmAutocompleteConfig({
      transformOptionToString: (option: Aptitude) => option.nom,
      transformValueToSearch: (option: Aptitude) => option.nom,
    }),
  ],
})
export class FormDetailObjectifsAptitudesComponent implements OnInit {
  readonly #etapeFormService = inject(EtapeDetailFormService);
  readonly #referentielStore = inject(ReferentielStore);
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);

  readonly referentiel = input.required<Référentiel>();
  readonly idsAptitudes = input.required<string[]>();
  readonly referentielFiltre = signal<Référentiel | null>(null);

  readonly aptitudesFiltrees = linkedSignal(() =>
    this.#aptitudeStore.entities().filter((x) => !this.idsAptitudes().includes(x.id)),
  );
  protected readonly _referentiels = linkedSignal(() => this.#referentielStore.referentielsActifs());

  protected readonly aptitudeActions: ExpansionPanelMenuAction<number>[] = [
    {
      icon: 'lucideTrash',
      label: "Supprimer l'aptitude de l'étape",
      action: (index) => this.#supprimerAptitude(index),
    },
  ];

  protected readonly form = this.#fb.group({
    aptitudes: this.#fb.array([]),
  });

  constructor() {
    effect(() => {
      const referentiel = this.referentiel();
      const filtre = this.referentielFiltre();
      this.#aptitudeStore.charger({ idCartographie: referentiel.idCartographie, idRéférentiel: filtre?.id });
    });
  }

  setReferentiel(referentiel: Référentiel) {
    this.referentielFiltre.set(referentiel);
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.aptitudes.value.map((x) => this.#etapeFormService.ajouterAptitude(x));
      this.#etapeFormService.submitÉtape();
      this.#sidepanelService.close();
    });
  }

  ajouterAptitude() {
    const fc = this.#fb.nonNullable.control<Aptitude | undefined>(undefined);
    this.aptitudes.push(fc);
  }

  #supprimerAptitude(index?: number) {
    if (!index) return;

    this.aptitudes.removeAt(index);
  }

  filterAptitudes(search: string) {
    const aptitudes = this.#aptitudeStore.entities().filter((x) => !this.idsAptitudes().includes(x.id));

    if (!search) this.aptitudesFiltrees.set(aptitudes);

    this.aptitudesFiltrees.set(aptitudes.filter((x) => x.nom.toLowerCase().includes(search.toLowerCase())));
  }

  get aptitudes() {
    return this.form.get(FormControlNames.APTITUDES) as FormArray<FormControl>;
  }
}
